import React from "react";
import {
  FiGlobe,
  FiMail,
  FiMap,
  FiMapPin,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import { FaFacebookF } from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import AssociationContactLink from "../ui/AssociationContactLink";
import AssociationMapPanel from "./AssociationMapPanel";

import { cn } from "../utils/associationsUtils";

export default function AssociationCard({
  association,
  isMapOpen = false,
  onToggleMap,
  delay = 0,
}) {
  const {
    title,
    category,
    description,
    contactLabel,
    phone,
    phoneHref,
    mail,
    websiteUrl,
    facebookUrl,
    address,
    gps,
    image,
    hasMap,
    mapsHref,
  } = association;

  const locationLabel = address || gps;

  return (
    <Reveal delay={delay}>
      <article className="group h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-xl dark:border-slate-800 dark:bg-slate-950">
        <div className="relative h-2 bg-gradient-to-r from-[#1277B9] via-sky-400 to-cyan-300" />

        <div className="flex h-full flex-col p-5 sm:p-6">
          <div className="flex flex-col gap-5 sm:flex-row">
            <div className="shrink-0">
              <div className="relative h-28 w-28 overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:h-32 sm:w-32">
                <img
                  src={image}
                  alt={title}
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  onError={(event) => {
                    event.currentTarget.src = "/img/no-image.jpg";
                  }}
                />

                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/10 to-transparent opacity-0 transition group-hover:opacity-100" />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  {category && (
                    <span className="inline-flex rounded-full bg-[#1277B9]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
                      {category}
                    </span>
                  )}

                  <h2 className="mt-3 text-xl font-bold uppercase tracking-wide text-slate-950 dark:text-white">
                    {title}
                  </h2>
                </div>
              </div>

              {description && (
                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {description}
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-1 flex-col justify-between gap-5">
            {(contactLabel || locationLabel) && (
              <div className="space-y-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/70">
                {contactLabel && (
                  <div className="flex items-start gap-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
                      <FiUser className="h-4 w-4" />
                    </span>

                    <span className="pt-2">{contactLabel}</span>
                  </div>
                )}

                {locationLabel && (
                  <div className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-[#1277B9] shadow-sm dark:bg-slate-950">
                      <FiMapPin className="h-4 w-4" />
                    </span>

                    {mapsHref ? (
                      <a
                        href={mapsHref}
                        target="_blank"
                        rel="noreferrer"
                        className="pt-2 font-semibold hover:text-[#1277B9] hover:underline"
                      >
                        {locationLabel}
                      </a>
                    ) : (
                      <span className="pt-2 font-semibold">
                        {locationLabel}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <AssociationContactLink
                href={phoneHref}
                icon={FiPhone}
                label={phone || "Téléphoner"}
              />

              <AssociationContactLink
                href={mail ? `mailto:${mail}` : ""}
                icon={FiMail}
                label="Email"
                variant={mail ? "primary" : "default"}
              />

              <AssociationContactLink
                href={websiteUrl}
                icon={FiGlobe}
                label="Site internet"
                external
              />

              <AssociationContactLink
                href={facebookUrl}
                icon={FaFacebookF}
                label="Facebook"
                external
              />

              {hasMap && (
                <button
                  type="button"
                  onClick={onToggleMap}
                  className={cn(
                    "inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border px-4 py-2 text-sm font-bold transition",
                    "focus:outline-none focus:ring-2 focus:ring-[#1277B9]/40",
                    isMapOpen
                      ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm hover:bg-[#0f659d]"
                      : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                  )}
                >
                  <FiMap className="h-4 w-4" />
                  {isMapOpen ? "Masquer la carte" : "Voir la carte"}
                </button>
              )}
            </div>
          </div>

          {isMapOpen && <AssociationMapPanel association={association} />}
        </div>
      </article>
    </Reveal>
  );
}